import { Box, Slide, Typography, Tabs, Tab, useTheme } from "@mui/material";
import React, { useState } from "react";
import { AllSideTabs } from "../../../../constants";
import { useLogger } from "../../../../contexts/LoggerContext";
import { PassingStats, TeamOverviewStats } from "../../../../types/LogTypes";
import { Passing } from "../../Home/Components/Passing";

const TeamStatsTab = () => {
  const theme = useTheme();
  const { gameLog } = useLogger();

  // If no game log data, show empty state
  if (!gameLog?.stats.user_club || !gameLog?.stats.opponent_club) {
    return (
      <Box sx={{ p: 3, width: "100%", maxWidth: 600, mx: "auto" }}>
        <Typography
          variant="body1"
          sx={{ color: "white", textAlign: "center" }}
        >
          No game data available
        </Typography>
      </Box>
    );
  }

  const userStats = gameLog.stats.user_club;
  const opponentStats = gameLog.stats.opponent_club;

  // Define the stats to display with their labels
  // add turnovers, takeaways, time of possession
  const statsToDisplay = [
    { key: "total_yards" as keyof TeamOverviewStats, label: "Total Yards" },
    { key: "passing_yards" as keyof TeamOverviewStats, label: "Passing Yards" },
    { key: "rushing_yards" as keyof TeamOverviewStats, label: "Rushing Yards" },
    { key: "first_downs" as keyof TeamOverviewStats, label: "First Downs" },
    { key: "penalties" as keyof TeamOverviewStats, label: "Penalties" },
    { key: "penalty_yards" as keyof TeamOverviewStats, label: "Penalty Yards" },
    {
      key: "third_downs_conv" as keyof TeamOverviewStats,
      label: "Third Downs Conv",
    },
    {
      key: "fourth_downs_conv" as keyof TeamOverviewStats,
      label: "Fourth Downs Conv",
    },
    {
      key: "rushing_attempts" as keyof TeamOverviewStats,
      label: "Rushing Attempts",
    },
    {
      key: "rushing_yards_per_attempt" as keyof TeamOverviewStats,
      label: "Rushing Yards Per Attempt",
    },
  ];

  return (
    <Box sx={{ p: 3, width: "100%", maxWidth: 800, mx: "auto" }}>
      {statsToDisplay.map(({ key, label }) => {
        const clubValue = userStats[key] as number;
        const oppValue = opponentStats[key] as number;
        const max = Math.max(clubValue, oppValue, 1); // avoid div by zero

        return (
          <Box
            key={key}
            sx={{ mb: 4, display: "flex", alignItems: "center", px: 2 }}
          >
            {/* Label on the left */}
            <Typography
              variant="subtitle1"
              sx={{
                width: 120,
                fontWeight: 600,
                color: "white",
                fontSize: 16,
                lineHeight: 1.2,
                mr: 2,
              }}
            >
              {label}
            </Typography>

            {/* Bars in the center */}
            <Box
              sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}
            >
              {/* Club bar */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  component="img"
                  src={userStats.club.profile_picture}
                  alt="Club Logo"
                  sx={{
                    height: 20,
                    width: "auto",
                    mr: 1,
                    borderRadius: "4px",
                  }}
                />
                <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: 8,
                      bgcolor: "#e3e3e3",
                      borderRadius: 2,
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.round((clubValue / max) * 100)}%`,
                        height: "100%",
                        bgcolor: theme.palette.stab,
                        borderRadius: 2,
                        transition: "width 0.3s",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              {/* Opponent bar */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  component="img"
                  src={opponentStats.club.profile_picture}
                  alt="Opponent Logo"
                  sx={{
                    height: 20,
                    width: "auto",
                    mr: 1,
                    borderRadius: "4px",
                  }}
                />
                <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: 8,
                      bgcolor: "#e3e3e3",
                      borderRadius: 2,
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.round((oppValue / max) * 100)}%`,
                        height: "100%",
                        bgcolor: "rgba(116, 115, 177, 1)",
                        borderRadius: 2,
                        transition: "width 0.3s",
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Values on the right */}
            <Box
              sx={{
                width: 60,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "white", fontWeight: 600, fontSize: 14 }}
              >
                {clubValue}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "white", fontWeight: 600, fontSize: 14 }}
              >
                {oppValue}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

interface OffenseStatsTabProps {
  passingStats?: PassingStats;
}

const OffenseStatsTab = ({ passingStats }: OffenseStatsTabProps) => {
  return (
    <Box>
      {passingStats && <Passing passingStats={passingStats} />}
      <Typography variant="h6">Rushing</Typography>
      <Typography variant="h6">Receiving</Typography>
    </Box>
  );
};

export const InGameStats = ({ open }: { open: boolean }) => {
  const [tab, setTab] = useState(0);
  const theme = useTheme();
  const { gameLog } = useLogger();

  return (
    <Slide
      direction="up"
      in={open}
      mountOnEnter
      unmountOnExit
      style={{ maxWidth: 1150 }}
    >
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          top: 145,
          height: "100%",
          bgcolor: theme.palette.background.paper,
          boxShadow: 3,
          zIndex: 1000,
          borderTop: 1,
          borderColor: "divider",
          width: "100%",
        }}
      >
        <Box
          sx={{
            borderBottom: (theme) => `2px solid ${theme.palette.divider}`,
            mb: 2,
          }}
        >
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: "rgba(123, 45, 58, 0.9)",
              },
              "& .MuiTab-root": {
                color: "rgba(255, 255, 255, 0.7)",
                fontWeight: 600,
                "&.Mui-selected": { color: "white" },
              },
            }}
          >
            <Tab label="TEAM" />
            {AllSideTabs.map((tab) => (
              <Tab key={tab.value} label={tab.label.toUpperCase()} />
            ))}
          </Tabs>
        </Box>
        <Box sx={{ width: "100%", height: "100%", overflowY: "auto" }}>
          {tab === 0 && <TeamStatsTab />}
          {tab === 1 && (
            <OffenseStatsTab passingStats={gameLog?.stats.passing} />
          )}
          {/* Add other tab content here as needed */}
        </Box>
      </Box>
    </Slide>
  );
};
